/**
 * MobileMenu — slide-down drawer for small-screen navigation.
 *
 * Purpose: Renders the full nav link list inside a collapsible panel that is
 *   toggled by ShopNav's hamburger button.
 * Inputs:  open (boolean), onClose (() => void), cartCount (number),
 *   signInUrl (string), signOutUrl (string | null — null means user is signed in)
 * Outputs: Accessible drawer with all nav links + Build Your Own CTA + account control.
 * Constraints: Tailwind utility-only; no extra deps. Esc key closes.
 */

import { useEffect, useRef } from 'react'
import { NavLink } from 'react-router'
import type { NavLinkRenderProps } from 'react-router'

interface MobileMenuProps {
  open: boolean
  onClose: () => void
  cartCount: number
  /** URL to send unauthenticated users to for sign-in. */
  signInUrl: string
  /** Present when user IS signed in — URL to call for sign-out. Null when signed out. */
  signOutUrl: string | null
}

/** className render-prop helper for NavLink — applies active gold underline. */
function mobileNavClass({ isActive }: NavLinkRenderProps) {
  return [
    'block px-4 py-3 text-sm font-medium text-white/80',
    'hover:text-white hover:bg-white/5 rounded-lg transition-colors',
    isActive ? 'text-gold underline underline-offset-4' : '',
  ]
    .filter(Boolean)
    .join(' ')
}

export function MobileMenu({ open, onClose, cartCount, signInUrl, signOutUrl }: MobileMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on Esc
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Focus first link when opened
  useEffect(() => {
    if (open && menuRef.current) {
      const first = menuRef.current.querySelector<HTMLElement>('a, button')
      first?.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        ref={menuRef}
        id="mobile-nav-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className="fixed top-[64px] left-0 right-0 z-50 border-t border-gold/10 bg-navy/95 backdrop-blur-md shadow-lg"
      >
        <nav
          aria-label="Mobile navigation"
          className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1"
        >
          {/* Build Your Own — primary CTA */}
          <NavLink
            to="/design"
            onClick={onClose}
            className={({ isActive }: NavLinkRenderProps) =>
              [
                'flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-colors',
                isActive
                  ? 'bg-gold text-white'
                  : 'bg-gold/10 text-gold hover:bg-gold/20 border border-gold/30',
              ].join(' ')
            }
          >
            <PencilIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
            Build Your Own
          </NavLink>

          <NavLink to="/products" onClick={onClose} className={mobileNavClass}>
            Shop
          </NavLink>

          <NavLink to="/catalog" onClick={onClose} className={mobileNavClass}>
            Catalog
          </NavLink>

          <NavLink to="/orders" onClick={onClose} className={mobileNavClass}>
            Orders
          </NavLink>

          {/* Account control */}
          {signOutUrl ? (
            <>
              <NavLink to="/account" onClick={onClose} className={mobileNavClass} aria-label="Your account">
                Account
              </NavLink>
              <a
                href={signOutUrl}
                className="block px-4 py-3 text-sm font-medium text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                onClick={onClose}
                aria-label="Sign out"
              >
                Sign out
              </a>
            </>
          ) : (
            <a
              href={signInUrl}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-navy bg-gold hover:bg-gold/90 transition-colors"
              onClick={onClose}
            >
              Sign in
            </a>
          )}

          <NavLink
            to="/cart"
            onClick={onClose}
            className={({ isActive }: NavLinkRenderProps) =>
              [
                'flex items-center gap-2 px-4 py-3 text-sm font-medium text-white/80',
                'hover:text-white hover:bg-white/5 rounded-lg transition-colors',
                isActive ? 'text-gold underline underline-offset-4' : '',
              ]
                .filter(Boolean)
                .join(' ')
            }
            aria-label={`Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`}
          >
            <CartBagIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
            Cart
            {cartCount > 0 && (
              <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-gold text-white text-[10px] font-bold leading-none">
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </NavLink>
        </nav>
      </div>
    </>
  )
}

/** Inline pencil / edit SVG for the Build Your Own CTA. */
function PencilIcon({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      {...props}
    >
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
  )
}

/** Inline shopping bag SVG — exported for use in ShopNav too. */
export function CartBagIcon({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 01-8 0" />
    </svg>
  )
}
